import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X, Cookie } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { grantAnalyticsConsent } from '@/lib/analytics';
import { onMarketingConsentGranted } from '@/lib/marketing';

/**
 * Cookie / essential-services consent banner.
 *
 * Behaviour, per the product spec:
 *   * Shown only on the *public* pages: /login, /portal/login, /kiosk/*
 *   * The current app only sets a strictly-necessary auth token + a
 *     service-worker registration — no analytics/marketing cookies — so
 *     acceptance is a single button, not per-category toggles.
 *   * Once accepted, the banner is hidden forever on this browser via a
 *     localStorage flag. The acceptance is posted to
 *     ``POST /api/public/consent`` so we have a server-side audit trail we
 *     can show regulators. That call is best-effort — a failure MUST NOT
 *     prevent the banner from dismissing.
 *
 * The banner is intentionally rendered globally in ``App.js`` so it
 * survives client-side route transitions and can't be forgotten on any
 * single public page.
 */
const LS_KEY = 'rotahq_cookie_consent_v1';
const PUBLIC_PATHS = ['/login', '/portal/login', '/kiosk', '/signup'];

function isPublicPath(pathname) {
  return PUBLIC_PATHS.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`),
  );
}

function alreadyAccepted() {
  try {
    return !!localStorage.getItem(LS_KEY);
  } catch {
    return false;
  }
}

function persistAcceptance() {
  try {
    localStorage.setItem(
      LS_KEY,
      JSON.stringify({ accepted: true, at: new Date().toISOString() }),
    );
  } catch {
    /* private browsing / quota — ignore, banner just re-shows next time */
  }
}

// Fire and forget — do NOT block the UI on the audit call.
async function logAcceptanceAsync(pathname) {
  const backend = process.env.REACT_APP_BACKEND_URL;
  if (!backend) return;
  try {
    await axios.post(
      `${backend}/api/public/consent`,
      { kind: 'cookie', accepted: true, path: pathname || '' },
      { timeout: 6000 },
    );
  } catch {
    /* audit is best-effort; a failed log must never affect the user */
  }
}

export function CookieBanner() {
  const { pathname } = useLocation();
  const [visible, setVisible] = useState(false);

  // Recompute visibility whenever the route changes.
  useEffect(() => {
    if (alreadyAccepted()) {
      setVisible(false);
      return;
    }
    setVisible(isPublicPath(pathname));
  }, [pathname]);

  const dismiss = useCallback(() => {
    persistAcceptance();
    grantAnalyticsConsent(); // unlocks GA4 analytics_storage (Consent Mode v2)
    onMarketingConsentGranted(); // loads Meta Pixel (if configured) post-consent
    logAcceptanceAsync(pathname); // fire-and-forget
    setVisible(false);
  }, [pathname]);

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
      className="fixed inset-x-3 bottom-3 z-[200] sm:inset-x-auto sm:right-4 sm:bottom-4 sm:max-w-md"
      data-testid="cookie-banner"
    >
      <div className="surface-card p-4 sm:p-5 border border-border shadow-lg backdrop-blur-md bg-[color:var(--brand-surface)]/95">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 flex-none items-center justify-center rounded-lg bg-[color:var(--brand-primary-soft)] text-[color:var(--brand-primary)]">
            <Cookie className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <h2
              id="cookie-banner-title"
              className="font-display font-semibold text-base leading-tight"
            >
              Cookies on RotaHQ
            </h2>
            <p
              id="cookie-banner-body"
              className="text-sm text-muted-foreground mt-1"
            >
              RotaHQ uses strictly-necessary cookies to keep you signed in and
              to run the offline-capable installer, plus privacy-friendly
              Google Analytics cookies (only after you accept) to help us
              understand how the app is used. We never use marketing or
              advertising cookies. See our{' '}
              <a
                href="/privacy"
                className="underline underline-offset-2 hover:text-foreground"
                data-testid="cookie-banner-privacy-link"
              >
                privacy policy
              </a>{' '}
              for details.
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              <Button
                onClick={dismiss}
                className="bg-[color:var(--brand-primary)] hover:bg-[color:var(--brand-primary-hover)]"
                data-testid="cookie-banner-accept"
                size="sm"
              >
                Accept &amp; continue
              </Button>
              <Button
                variant="ghost"
                onClick={dismiss}
                size="sm"
                data-testid="cookie-banner-dismiss"
                aria-label="Dismiss cookie banner"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
